import {
    useEffect,
    useMemo,
    useRef,
} from 'react';
import {
    getBezierPath,
    Position,
    useStore,
} from 'react-flow-renderer';

import getStateVisuals from './nodes/stateVisuals';

const CORE_RADIUS = 3.5;
const HALO_RADIUS = 8;
const TRAIL_WIDTH = 2.5;
const DASH_SIZE = 6;
const GAP_SIZE = 4;
const BASE_STROKE = '#c5c9d4';

const selectNodeInternals = (state) => state.nodeInternals;
const selectTransform = (state) => state.transform;

const getAnchor = (node, handleId, role) => {
    const x = node.positionAbsolute.x;
    const y = node.positionAbsolute.y + (node.height / 2);

    if (handleId === 'start') {
        return { position: Position.Left, x, y };
    }

    if (handleId === 'end') {
        return { position: Position.Right, x: x + node.width, y };
    }

    if (role === 'source') {
        return { position: Position.Right, x: x + node.width, y };
    }

    return { position: Position.Left, x, y };
};

const getEdgePath = (source, target, edge) => {
    const sourceAnchor = getAnchor(source, edge.sourceHandle, 'source');
    const targetAnchor = getAnchor(target, edge.targetHandle, 'target');

    return getBezierPath({
        sourcePosition: sourceAnchor.position,
        sourceX: sourceAnchor.x,
        sourceY: sourceAnchor.y,
        targetPosition: targetAnchor.position,
        targetX: targetAnchor.x,
        targetY: targetAnchor.y,
    });
};

const buildRemainingDasharray = (travelled, length) => {
    const remaining = Math.max(0, length - travelled);

    if (remaining <= 0.5) return `0 ${Math.max(length, 1)}`;

    const pattern = [];
    let covered = 0;

    while (covered < remaining + DASH_SIZE + GAP_SIZE) {
        pattern.push(DASH_SIZE, GAP_SIZE);
        covered += DASH_SIZE + GAP_SIZE;
    }

    return `0 ${travelled} ${pattern.join(' ')} ${length}`;
};

/**
 * Draws the dashed remainder and the solid trail as two complementary segments
 * of the same React Flow bezier. The dashed segment never sits under the trail.
 */
const VerificationSignals = ({
    edges,
    getEdgeTrails,
    isRunning,
}) => {
    const nodeInternals = useStore(selectNodeInternals);
    const transform = useStore(selectTransform);
    const pathRefs = useRef(new Map());
    const dashedRefs = useRef(new Map());
    const trailRefs = useRef(new Map());
    const signalRefs = useRef(new Map());

    const paths = useMemo(() => (
        edges.reduce((edgePaths, edge) => {
            const source = nodeInternals.get(edge.source);
            const target = nodeInternals.get(edge.target);

            if (!source?.width || !target?.width) return edgePaths;
            if (!source.positionAbsolute || !target.positionAbsolute) return edgePaths;

            return [
                ...edgePaths,
                {
                    d: getEdgePath(source, target, edge),
                    id: edge.id,
                    opacity: edge.opacity,
                    stroke: edge.baseStroke || BASE_STROKE,
                },
            ];
        }, [])
    ), [edges, nodeInternals]);

    const hiddenEdgeIds = useMemo(() => (
        edges.filter((edge) => !edge.isVisible).map((edge) => edge.id)
    ), [edges]);

    useEffect(() => {
        let frame = null;

        const paint = () => {
            const trails = getEdgeTrails();

            pathRefs.current.forEach((path, edgeId) => {
                const trail = trails.find((active) => active.edgeId === edgeId);
                const dashedElement = dashedRefs.current.get(edgeId);
                const trailElement = trailRefs.current.get(edgeId);
                const signalElement = signalRefs.current.get(edgeId);
                const pathRecord = paths.find((item) => item.id === edgeId);
                const isHidden = hiddenEdgeIds.includes(edgeId);

                if (!dashedElement || !trailElement || !signalElement) return;

                if (isHidden) {
                    dashedElement.style.opacity = '0';
                    trailElement.style.opacity = '0';
                    signalElement.style.opacity = '0';

                    return;
                }

                const length = path.getTotalLength();
                const progress = trail?.progress ?? 0;
                const travelled = length * progress;
                const opacity = String(pathRecord?.opacity ?? 1);
                const color = trail
                    ? getStateVisuals(trail.state).stroke
                    : BASE_STROKE;

                dashedElement.setAttribute('stroke', pathRecord?.stroke || BASE_STROKE);
                dashedElement.setAttribute(
                    'stroke-dasharray',
                    buildRemainingDasharray(travelled, length)
                );
                dashedElement.style.opacity = opacity;

                if (!trail || progress <= 0) {
                    trailElement.style.opacity = '0';
                    signalElement.style.opacity = '0';

                    return;
                }

                trailElement.setAttribute('stroke', color);
                trailElement.setAttribute('stroke-dasharray', `${travelled} ${Math.max(length, 1)}`);
                trailElement.style.opacity = opacity;

                const point = path.getPointAtLength(travelled);

                signalElement.setAttribute('transform', `translate(${point.x}, ${point.y})`);
                signalElement.setAttribute('fill', color);
                signalElement.style.opacity = trail.showSignal ? '1' : '0';
            });

            if (isRunning) frame = requestAnimationFrame(paint);
        };

        paint();

        return () => {
            if (frame) cancelAnimationFrame(frame);
        };
    }, [
        getEdgeTrails,
        hiddenEdgeIds,
        isRunning,
        paths,
    ]);

    return (
        <svg
            style={{
                height: '100%',
                left: 0,
                overflow: 'visible',
                pointerEvents: 'none',
                position: 'absolute',
                top: 0,
                width: '100%',
                zIndex: 4,
            }}
        >
            <g transform={`translate(${transform[0]}, ${transform[1]}) scale(${transform[2]})`}>
                {paths.map((path) => (
                    <g key={path.id}>
                        <path
                            d={path.d}
                            fill="none"
                            ref={(element) => {
                                if (element) pathRefs.current.set(path.id, element);
                                else pathRefs.current.delete(path.id);
                            }}
                            stroke="none"
                        />

                        <path
                            d={path.d}
                            fill="none"
                            ref={(element) => {
                                if (element) dashedRefs.current.set(path.id, element);
                                else dashedRefs.current.delete(path.id);
                            }}
                            stroke={path.stroke}
                            strokeDasharray={`${DASH_SIZE} ${GAP_SIZE}`}
                            strokeLinecap="round"
                            strokeWidth={TRAIL_WIDTH}
                            style={{ opacity: path.opacity }}
                        />

                        <path
                            d={path.d}
                            fill="none"
                            ref={(element) => {
                                if (element) trailRefs.current.set(path.id, element);
                                else trailRefs.current.delete(path.id);
                            }}
                            strokeLinecap="round"
                            strokeWidth={TRAIL_WIDTH}
                            style={{ opacity: 0 }}
                        />

                        <g
                            opacity={0}
                            ref={(element) => {
                                if (element) signalRefs.current.set(path.id, element);
                                else signalRefs.current.delete(path.id);
                            }}
                        >
                            <circle fillOpacity={0.2} r={HALO_RADIUS} />
                            <circle r={CORE_RADIUS} />
                        </g>
                    </g>
                ))}
            </g>
        </svg>
    );
};

export default VerificationSignals;
